import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Plus, Trash2, Minus } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ConnectedAreas from "../../components/os/ConnectedAreas";
import { api, formatApiError, mediaUrl } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const money = (n, c = "USD") => `$${Number(n || 0).toLocaleString()} ${c}`;

export default function AuraProject() {
  const { gid } = useParams();
  const [group, setGroup] = useState(null);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [pick, setPick] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/group/${gid}`).then((r) => setGroup(r.data)).catch(() => {});
    api.get(`/projects/${gid}/aura/items`).then((r) => setItems(r.data)).catch(() => {});
  }, [gid]);
  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    try { const { data } = await api.get(`/projects/${gid}/aura/catalog`); setCatalog(data); setPick(true); }
    catch (e) { toast.error("Error"); }
  };
  const addItem = async (p) => {
    try { await api.post(`/projects/${gid}/aura/items`, { product_id: p.id, qty: 1 }); toast.success("Añadido al proyecto"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const setQty = async (it, qty) => { if (qty < 1) return; await api.patch(`/projects/${gid}/aura/items/${it.id}`, { qty }); load(); };
  const del = async (it) => { await api.delete(`/projects/${gid}/aura/items/${it.id}`); load(); };

  const total = items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.qty) || 0), 0);
  const currency = items[0]?.currency || "USD";

  return (
    <OSLayout title={group ? `AURA · ${group.name}` : "AURA Commerce"}>
      <div className="space-y-6">
        <ConnectedAreas module="aura" entityId={gid} />

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-navy/70"><ShoppingBag className="w-5 h-5" /><h2 className="text-xl font-medium">Productos que el proyecto necesita</h2></div>
            <p className="text-sm text-navy/50 mt-1">Selecciona del catálogo AURA los productos a adquirir para este proyecto.</p>
          </div>
          <button data-testid="ap-add-open" onClick={openPicker} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity"><Plus className="w-4 h-4" /> Añadir producto</button>
        </div>

        {items.length === 0 ? (
          <div className="text-navy/40 py-10 text-center border border-dashed border-navy/15 rounded-lg" data-testid="aura-project-empty">Aún no has añadido productos a este proyecto.</div>
        ) : (
          <>
            <div className="space-y-2" data-testid="aura-project-items">
              {items.map((it) => (
                <motion.div key={it.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}
                  data-testid={`ap-item-${it.id}`} className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg p-3">
                  <div className="w-12 h-12 rounded-lg bg-cream overflow-hidden shrink-0 border border-navy/10">
                    {it.cover ? <img src={mediaUrl(it.cover)} alt={it.name} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="text-[11px] text-navy/45">{money(it.price, it.currency)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`ap-minus-${it.id}`} onClick={() => setQty(it, it.qty - 1)} className="w-7 h-7 rounded-lg border border-navy/10 flex items-center justify-center text-navy/50 hover:border-violet/40 transition-colors"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center text-sm">{it.qty}</span>
                    <button data-testid={`ap-plus-${it.id}`} onClick={() => setQty(it, it.qty + 1)} className="w-7 h-7 rounded-lg border border-navy/10 flex items-center justify-center text-navy/50 hover:border-violet/40 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="w-28 text-right text-sm font-medium">{money((Number(it.price) || 0) * (Number(it.qty) || 0), it.currency)}</div>
                  <button data-testid={`ap-del-${it.id}`} onClick={() => del(it)} className="text-navy/30 hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                </motion.div>
              ))}
            </div>
            <div className="flex justify-end items-center gap-3 pt-2 border-t border-navy/10">
              <span className="text-sm text-navy/50">Total estimado</span>
              <span className="text-lg font-medium" data-testid="aura-project-total">{money(total, currency)}</span>
            </div>
          </>
        )}
      </div>

      {pick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setPick(false)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
            onClick={(e) => e.stopPropagation()} data-testid="aura-picker-modal" className="w-full max-w-2xl bg-cream rounded-xl p-8 max-h-[85vh] overflow-y-auto thin-scroll">
            <h3 className="text-xl font-medium mb-5">Catálogo AURA</h3>
            {catalog.length === 0 ? (
              <div className="text-sm text-navy/40 py-6 text-center">No hay productos en el catálogo AURA todavía.</div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {catalog.map((p) => (
                  <button key={p.id} data-testid={`ap-catalog-${p.id}`} onClick={() => addItem(p)}
                    className="text-left bg-white border border-navy/10 rounded-lg overflow-hidden hover:border-violet/40 transition-colors">
                    <div className="aspect-square bg-cream overflow-hidden">{p.cover ? <img src={mediaUrl(p.cover)} alt={p.name} className="w-full h-full object-cover" /> : null}</div>
                    <div className="p-3">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-navy/45">{money(p.price, p.currency)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </OSLayout>
  );
}
